// Network status detection hook
// Uses navigator.onLine - the /version.json ping was unreliable on mobile/slow networks

import { useState, useEffect, useCallback } from "react";

/**
 * Hook to detect online/offline status
 * Uses navigator.onLine as the signal (simple and reliable)
 * @returns {{ isOnline: boolean, isChecking: boolean, checkNow: function }}
 */
export function useOnlineStatus() {
  // Default to true - assume online unless browser explicitly says otherwise
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  const checkConnectivity = useCallback(() => {
    const online = navigator.onLine;
    setIsOnline(online);
    return online;
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return {
    isOnline,
    isChecking: false,
    checkNow: checkConnectivity,
  };
}

export default useOnlineStatus;
